// What a reply promises (2026-10-23, docs/agent-draft-shadow.md, "Keep
// promise and action together"; docs/decisions/2026-10-23-agent-commit-to-actions.md).
//
// A customer-facing reply may say that a refund, a replacement, a return
// label or a cancellation is happening only when the step that does it runs
// with the reply. findPromisedActions() reads a reply for those four kinds of
// promise; promiseSendCheck() compares them with the suggestion's proposed
// steps and the steps already processed, for the Send guard
// (server/src/routes/ai.js) and the primary agent draft's check
// (server/src/ai/agent-draft-primary.js).
//
// Detection is narrow on purpose, so normal sends are never blocked by it:
//   - only first-person commitments ("we're sending", "we will refund",
//     "we've cancelled"), "you'll receive a prepaid return label" and a few
//     passive forms ("your order has been cancelled", "a replacement is being
//     sent");
//   - never a question, and never a sentence with a condition or an offer in
//     it (if, once, when, after, as soon as, unless, would you like, ...), so
//     template boilerplate such as "If you'd like a replacement, reply to this
//     message" or "Once we receive the return, we'll issue your refund" is
//     not a promise;
//   - never what happens after a future event (2026-10-27, prod contact
//     7984986): "before" / "pending" / "following" an event, a time window
//     anchored to one ("within 2 business days of inspection"), a
//     "Refund Timeline:" line, a sentence that waits on the customer
//     ("Please reply with ... and we'll ..."), an offer on an options list
//     ("Refund now — we'll process your refund right away."), and any
//     refund, replacement, return label or cancellation in a reply that
//     says the event has not happened yet ("has not been inspected", "once
//     we receive");
//   - a replacement must be "a / another replacement", "the correct ..." or
//     "a new one": "your replacement will ship ..." is about a replacement
//     that already exists (an order status reply), not a new promise.
// Pure: no calls, no dependencies.

export const PROMISE_KINDS = Object.freeze(['replacement', 'refund', 'return_label', 'cancellation'])

// The words the Send guard uses for each kind: "This reply says <words>, ...".
export const PROMISE_KIND_WORDS = Object.freeze({
  replacement: 'a replacement is being sent',
  refund: 'a refund is being issued',
  return_label: 'a return label is being sent',
  cancellation: 'the order is being cancelled',
})
// The words for a statement that it already happened (2026-10-28): "the
// reply says the refund was already processed, but the order shows no
// refund" (the agent draft's check of a 'done' statement against the order's
// facts, server/src/ai/agent-draft.js's donePromisesNotShown()).
export const PROMISE_DONE_WORDS = Object.freeze({
  replacement: 'a replacement was already sent',
  refund: 'the refund was already processed',
  return_label: 'a return label was already sent',
  cancellation: 'the order was already cancelled',
})
// The step's name, for "no <name> step is on this contact".
export const PROMISE_STEP_NAMES = Object.freeze({
  replacement: 'replacement',
  refund: 'refund',
  return_label: 'return label',
  cancellation: 'cancellation',
})

// The proposed-step types that carry out each kind. A message, note or
// advice step never does (advise_*, send_*_message, add_order_note).
// process_return_refund counts for a refund and for a return label: its
// Website RMA path creates the label.
export const PROMISE_STEP_TYPES = Object.freeze({
  replacement: Object.freeze(['process_replacement', 'create_replacement_trq', 'inr_create_replacement_trq']),
  refund: Object.freeze(['process_return_refund', 'issue_ebay_return_refund', 'issue_partial_return_refund_ebay', 'inr_issue_refund', 'task_create_refund']),
  return_label: Object.freeze(['create_return_label_for_replacement', 'resend_return_label', 'automated_return_notice_resend_label', 'process_return_refund']),
  cancellation: Object.freeze(['cancel_order_trq', 'create_ebay_cancellation', 'approve_ebay_cancellation', 'sales_hold_cancel_order_trq', 'sales_hold_cancel_order_ebay']),
})

// The kinds a step type carries out ([] for any other step).
export function stepPromiseKinds(type) {
  const t = String(type ?? '')
  return PROMISE_KINDS.filter((k) => PROMISE_STEP_TYPES[k].includes(t))
}

// A return step that makes no return label (2026-10-29, Jenn;
// docs/decisions/2026-10-29-agent-draft-primary-return-status-refund.md): a
// process_return_refund whose checked plan (params.checkResult.plannedSteps,
// server/src/ai/return-refund.js's buildPlannedSteps()) has no label call.
// The scrap check's refund with no return (an RTN), a customer who ships
// with their own label and a part already shipped back make none. A step
// not checked yet (no plan) counts as making one, as before.
const LABEL_CALL_RE = /\/shipping\/return\/label$/
export function returnStepMakesNoLabel(action) {
  if (action?.type !== 'process_return_refund') return false
  const planned = action.params?.checkResult?.plannedSteps
  if (!Array.isArray(planned) || !planned.length) return false
  return !planned.some((p) => LABEL_CALL_RE.test(String(p?.apiCall?.url ?? '')))
}

// The kinds a proposed step carries out: its type's (stepPromiseKinds()),
// less the return label for a return step that makes none
// (returnStepMakesNoLabel()). The ONE rule Send's promise check and the
// agent draft's check both read.
export function actionPromiseKinds(action) {
  const kinds = stepPromiseKinds(action?.type)
  return returnStepMakesNoLabel(action) ? kinds.filter((k) => k !== 'return_label') : kinds
}

// A step a person can Process: the same test as the panel's Process button
// (a target system; select_reason_code only collects input).
export function isProcessableStep(action) {
  return Boolean(action?.type) && Boolean(action.targetSystem) && action.type !== 'select_reason_code'
}

// Whether a step makes an API call: its own, or one in its planned steps or
// branches (the MCP trace's hasApiCall).
export function stepHasApiCall(action) {
  if (action?.apiCall) return true
  if ((action?.plannedSteps ?? []).some((s) => s?.apiCall)) return true
  return (action?.branches ?? []).some((b) => b?.apiCall || (b?.steps ?? []).some((s) => s?.apiCall))
}

// A console step (2026-10-27, docs/decisions/2026-10-27-manual-steps-
// confirm-not-block.md): a step with a target system but no API call. CXHub
// cannot process it; the person does it in the console. A reply that
// promises what it does is never blocked and never asks for a confirmation:
// Send just sends (docs/decisions/2026-10-27-no-manual-step-confirm.md).
// Every step CXHub does process carries its call (server/src/ai/
// step-call-previews.js), so this is never a step the executor runs. An
// eBay attach step (params.attachToEbayReturn, 2026-10-27) is always one
// CXHub runs (the upload and UPLOAD_LABEL calls), so it keeps the block.
export function isManualStep(action) {
  return isProcessableStep(action) && !stepHasApiCall(action) && action?.params?.attachToEbayReturn !== true
}

// --- Detection -------------------------------------------------------------

const WE = String.raw`\b(?:we|i)`
// "we'll", "we will", "we are going to", "we'll be" (+ -ing).
const FUT = String.raw`${WE}(?:'ll|\s+will|\s+shall|(?:'re|\s+are|'m|\s+am)\s+going\s+to)\s+(?:also\s+|now\s+|then\s+|immediately\s+|promptly\s+|go\s+ahead\s+and\s+)?(?:be\s+)?`
const PROG = String.raw`${WE}(?:'re|\s+are|'m|\s+am)\s+(?:also\s+|now\s+|currently\s+|already\s+|happy\s+to\s+(?:be\s+)?)?`
const PERF = String.raw`${WE}(?:'ve|\s+have)\s+(?:also\s+|now\s+|already\s+|just\s+|gone\s+ahead\s+and\s+)?`

// A verb's base, -ing and past participle forms.
function verbs(list) {
  return {
    base: list.map((v) => v[0]).join('|'),
    ing: list.map((v) => v[1]).join('|'),
    pp: list.map((v) => v[2]).join('|'),
  }
}
const SEND = verbs([['send', 'sending', 'sent'], ['ship', 'shipping', 'shipped'], ['process', 'processing', 'processed'], ['create', 'creating', 'created'], ['mail', 'mailing', 'mailed'], ['reship', 'reshipping', 'reshipped'], ['issue', 'issuing', 'issued'], ['get', 'getting', 'gotten']])
const ISSUE = verbs([['issue', 'issuing', 'issued'], ['process', 'processing', 'processed'], ['send', 'sending', 'sent'], ['give', 'giving', 'given'], ['submit', 'submitting', 'submitted'], ['initiate', 'initiating', 'initiated'], ['apply', 'applying', 'applied']])
const LABEL_V = verbs([['send', 'sending', 'sent'], ['email', 'emailing', 'emailed'], ['e-mail', 'e-mailing', 'e-mailed'], ['provide', 'providing', 'provided'], ['create', 'creating', 'created'], ['issue', 'issuing', 'issued'], ['include', 'including', 'included'], ['attach', 'attaching', 'attached'], ['generate', 'generating', 'generated']])
const CANCEL_V = verbs([['cancel', 'cancell?ing', 'cancell?ed']])
const REFUND_V = verbs([['refund', 'refunding', 'refunded']])
const APPROVE_V = verbs([['process', 'processing', 'processed'], ['approve', 'approving', 'approved'], ['accept', 'accepting', 'accepted'], ['complete', 'completing', 'completed'], ['submit', 'submitting', 'submitted']])

// The forms, each with its tense: 'future' (it is happening or will happen)
// or 'done' (it has happened).
function active(v, tail) {
  return [
    { re: new RegExp(String.raw`${FUT}(?:${v.base}|${v.ing})\b${tail}`, 'i'), tense: 'future' },
    { re: new RegExp(String.raw`${PROG}(?:${v.ing})\b${tail}`, 'i'), tense: 'future' },
    { re: new RegExp(String.raw`${PERF}(?:${v.pp})\b${tail}`, 'i'), tense: 'done' },
  ]
}
function passive(subject, pp) {
  return [
    { re: new RegExp(String.raw`${subject}\s+(?:will\s+be|'ll\s+be|is\s+being|are\s+being|is\s+now\s+being)\s+(?:now\s+|being\s+)?(?:${pp})\b`, 'i'), tense: 'future' },
    { re: new RegExp(String.raw`${subject}\s+(?:has\s+been|have\s+been|was|were|is|are)\s+(?:now\s+|already\s+|just\s+)?(?:${pp})\b`, 'i'), tense: 'done' },
  ]
}

// A new replacement, never "your / the replacement" (one that exists).
const NEW_REPLACEMENT = String.raw`(?:(?:a|an|another)\s+(?:free\s+|new\s+|brand\s+new\s+)?replacement\b|the\s+(?:correct|right)\b|(?:a\s+)?new\s+one\b|another\s+one\b|a\s+new\s+(?:part|unit|item)\b)`
const LABEL = String.raw`(?:(?:prepaid|pre-paid|free)\s+(?:return\s+)?(?:shipping\s+)?label|return\s+(?:shipping\s+)?label)\b`
// The one definition of a return label (2026-10-26): "return label", "return
// shipping label", "prepaid label", "free label". Never "shipping label"
// alone, which is the outbound package's. situation-brief.js's reply check
// imports it, so the two checks cannot disagree.
export const RETURN_LABEL_SRC = LABEL
const BETWEEN = (n) => String.raw`(?:\s+[\w'$.,-]+){0,${n}}?`

const PATTERNS = Object.freeze({
  replacement: [
    ...active(SEND, String.raw`(?:\s+(?:you|it))?(?:\s+out)?\s+${NEW_REPLACEMENT}`),
    { re: new RegExp(String.raw`\byou(?:'ll|\s+will)\s+(?:also\s+)?(?:receive|get|be\s+receiving|be\s+getting)\s+${NEW_REPLACEMENT}`, 'i'), tense: 'future' },
    ...passive(String.raw`\b(?:a|an)\s+(?:free\s+|new\s+)?replacement(?:\s+(?:order|part|unit))?`, SEND.pp),
    ...passive(String.raw`\bthe\s+(?:correct|right)(?:\s+[\w-]+){1,3}`, SEND.pp),
  ],
  refund: [
    ...active(REFUND_V, ''),
    ...active(ISSUE, String.raw`${BETWEEN(3)}\s+refund\b(?!\s+(?:request|policy|claim|eligibility|status))`),
    { re: new RegExp(String.raw`\byou(?:'ll|\s+will)\s+(?:also\s+)?(?:receive|get|see|be\s+receiving|be\s+getting)\s+(?:a|an)\s+(?:full\s+|partial\s+)?refund\b`, 'i'), tense: 'future' },
    { re: new RegExp(String.raw`\byou(?:'ll|\s+will)\s+(?:be\s+)?(?:fully\s+|partially\s+)?refunded\b`, 'i'), tense: 'future' },
    // An amount may stand before it (2026-10-28): "your $45.00 refund was
    // processed on 10/26/2026".
    ...passive(String.raw`\b(?:a|an|the|your)\s+(?:\$\s?\d[\d,]*(?:\.\d{2})?\s+)?(?:full\s+|partial\s+)?refund`, String.raw`issued|processed|sent|applied|credited|initiated|submitted`),
    ...passive(String.raw`\b(?:you|your\s+(?:order|purchase|payment)|the\s+(?:order|purchase|payment))`, String.raw`(?:fully\s+|partially\s+)?refunded`),
  ],
  return_label: [
    ...active(LABEL_V, String.raw`${BETWEEN(4)}\s+${LABEL}`),
    { re: new RegExp(String.raw`\byou(?:'ll|\s+will)\s+(?:also\s+)?(?:receive|get|be\s+receiving|be\s+getting|find)\s+(?:a|an|the|your)\s+${LABEL}`, 'i'), tense: 'future' },
    ...passive(String.raw`\b(?:a|an|the|your)\s+${LABEL}`, LABEL_V.pp),
  ],
  cancellation: [
    ...active(CANCEL_V, ''),
    ...active(APPROVE_V, String.raw`${BETWEEN(2)}\s+cancell?ation\b`),
    ...passive(String.raw`\b(?:your|the|this)\s+(?:order|purchase|transaction)`, CANCEL_V.pp),
    ...passive(String.raw`\b(?:your|the|this)\s+cancell?ation(?:\s+request)?`, APPROVE_V.pp),
  ],
})

// A return-label statement (2026-10-28, a template that needs a step: the
// CDR Fit NO / Listing Error / Defective templates, Label Exception, Cancel
// No Already Shipped): "Check your email for a prepaid UPS return label",
// "your return label has been sent / emailed". Whether it is still to happen
// depends on the order, not on the words: with no return label on the order
// it is 'future' (the step that sends it must run), with one it is 'done';
// unknown, the words decide ("Check your email for ..." future, "has been
// sent" done). Read on the sentence's first clause (the templates go on
// "— if it's not in your inbox, check your spam ..."), and never set aside by
// a reply that says the return has not arrived yet ("Once we receive and
// inspect the return, your refund will post ..." in the same template).
const STMT_LABEL = String.raw`(?:(?:free\s+)?(?:prepaid|pre-paid)\s+(?:UPS\s+|USPS\s+|FedEx\s+)?(?:return\s+)?(?:shipping\s+)?label|(?:UPS\s+|USPS\s+|FedEx\s+)?return\s+(?:shipping\s+)?label)\b`
const LABEL_STATEMENTS = Object.freeze([
  { re: new RegExp(String.raw`\bcheck\s+your\s+(?:e-?mail|inbox)(?:\s+inbox)?\s+for\s+(?:a|an|the|your)\s+${STMT_LABEL}`, 'i'), tense: 'future' },
  { re: new RegExp(String.raw`\b(?:a|an|the|your)\s+${STMT_LABEL}\s+(?:has|have)\s+(?:now\s+|already\s+|just\s+)?been\s+(?:sent|e-?mailed)\b`, 'i'), tense: 'done' },
])
function statementClause(sentence) {
  return String(sentence ?? '').split(/\s+[—–]\s+|\s+-\s+/)[0].trim()
}
// The tense of a return-label statement in this sentence, or null.
function labelStatementTense(sentence, returnLabelOnOrder) {
  const head = statementClause(sentence)
  if (!head || head.endsWith('?') || NOT_A_PROMISE_RE.test(head) || CUSTOMER_FIRST_RE.test(head)) return null
  const hit = LABEL_STATEMENTS.find((p) => p.re.test(head))
  if (!hit) return null
  if (returnLabelOnOrder === true) return 'done'
  if (returnLabelOnOrder === false) return 'future'
  return hit.tense
}

// A sentence that is a question, an offer or a condition is never a promise.
// A condition includes what happens before, pending or following an event
// (2026-10-27, prod contact 7984986: "... which is required before your
// refund is issued"). "before" a day or a date ("before Friday") and "the
// following" (a list) and "following up" are still promises.
const NOT_A_PROMISE_RE = /\b(?:if|once|when|whenever|after|as soon as|upon|unless|provided|whether|until|in case|pending|prior to|would you|do you|can you|could you|should you|let us know|feel free|you may|you can|we can|we could|we may|we might|eligible|policy|option)\b|\bbefore\b(?!\s+(?:the\s+end\s+of\s+)?(?:today|tonight|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday|noon|\d))|(?<!\bthe\s)\bfollowing\b(?!\s*:|\s+up\b)/i

// The future events a refund, a replacement, a label or a cancellation can be
// tied to (2026-10-27): the warehouse inspects or receives the return, it is
// delivered back, it is processed or approved, "we get it back".
const EVENT = String.raw`(?:inspection|inspected|inspecting|inspect|receipt|received|receive|receiving|arrival|arrives|arrived(?:\s+at\s+our\s+warehouse)?|delivery|delivered(?:\s+(?:back\s+)?to\s+(?:us|our\s+warehouse))?|processed|processing|approval|approved|we\s+get\s+it\s+back)\b`
const DAYS = String.raw`(?:\d+|one|two|three|four|five|six|seven|ten)(?:\s*[-–—]\s*(?:\d+|two|three|five|seven|ten))?\s+(?:business\s+|working\s+|calendar\s+)?days?`
// A time window anchored to one: "within 2 business days of inspection",
// "within 3–5 business days of receiving the item", "5 days after it is
// delivered to us". Up to four words between the anchor and the event.
const EVENT_WINDOW_RE = new RegExp(String.raw`\b(?:within\s+${DAYS}\s+(?:of|after|from)|${DAYS}\s+after)(?:\s+[\w'’-]+){0,4}?\s+${EVENT}`, 'i')

// A reply that says the event has not happened yet (2026-10-27): then every
// promise sentence in it, of every kind (refund, replacement, return label,
// cancellation), is about later ("your item arrived but has not been
// inspected ... We'll issue your refund within 2 business days of
// inspection"). Never "you haven't received it" (the customer waiting on
// the refund itself, "If you still haven't received it by then, let us
// know"), and never a sentence with "if" in it.
const NOT_YET_RE = /(?<!\byou\s+(?:still\s+)?)\b(?:has|have|had)(?:n['’]t|\s+not)\s+(?:yet\s+)?(?:been\s+)?(?:inspected|received|arrived|delivered|processed)\b|(?<!\byou\s+(?:have\s+|['’]ve\s+)?(?:still\s+)?)\bnot\s+yet\s+(?:been\s+)?(?:inspected|received|arrived|delivered|processed)\b|\bonce\s+(?:we|it|they)\s+(?:receive|get|inspect|arrives?|(?:is|are)\s+(?:received|inspected|delivered))\b|\bonce\s+(?:we\s+have|we['’]ve)\s+(?:received|inspected)\b|\bonce\s+received\b/i

// A "Refund Timeline:" line is policy text, never a promise (2026-10-27).
// A label alone on its line also takes the line after it.
const POLICY_LINE_RE = /^\s*refund\s+timeline\s*:/i

// The customer acts first (2026-10-27, the PO Box templates): "Please reply
// with a valid physical address and we'll get a replacement order processed
// and shipped right away" is about after the customer replies.
const CUSTOMER_FIRST_RE = /\b(?:reply|respond)\s+(?:with|to\s+confirm|and)\b|\bplease\s+(?:reply|respond|confirm|send|provide)\b|\bjust\s+(?:reply|confirm|send)\b|\bsend\s+us\b/i

// An options list (2026-10-27, the "Your Options" templates): in a reply
// that offers options, a line that starts with a short option label and a
// dash ("Refund now — we'll process your refund right away.") is an offer,
// so it promises nothing that is still to happen. A done statement on it
// ("We've already emailed a free prepaid return label") still counts.
const OPTIONS_RE = /\byour\s+options\b|\boption\s+\d\b|\bsolutions\s*:/i
const OPTION_LINE_RE = /^(?:option\s+\d\s*:\s*)?[\w'’ ]{1,40}\s+[—–-]\s/i

// "Being reviewed" (2026-10-27): a refund on an item already returned is a
// Support task; the reply says it is being reviewed by our team, which is
// never a promise.
const UNDER_REVIEW_RE = /\b(?:being|is|are)\s+(?:now\s+)?reviewed\b|\bunder\s+review\b/i

// An email reply box holds HTML: block ends become line breaks, other tags
// go, and the entities an apostrophe or a space is written with are decoded.
function plainText(text) {
  return String(text ?? '')
    .replace(/<br\s*\/?>|<\/(?:p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(?:#39|#x27|apos|rsquo|lsquo|#8217|#8216);/gi, "'")
    .replace(/&(?:nbsp|#160);/gi, ' ')
    .replace(/&amp;/gi, '&')
}

function linesOf(text) {
  return plainText(text)
    .replace(/[‘’]/g, "'")
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

function sentencesOfLine(line) {
  return line.split(/(?<=[.!?;])\s+/).map((s) => s.trim()).filter(Boolean)
}

// The sentences that can hold a promise, [{ text, offer }]: a "Refund
// Timeline:" line (and the line after a bare "Refund Timeline:" label) is
// left out; offer marks a sentence on an option line (OPTION_LINE_RE) of a
// reply that offers options.
function sentencesOf(text) {
  const out = []
  const lines = linesOf(text)
  const offersOptions = lines.some((l) => OPTIONS_RE.test(l))
  let skipNext = false
  for (const line of lines) {
    if (skipNext) { skipNext = false; continue }
    if (POLICY_LINE_RE.test(line)) {
      skipNext = !line.replace(POLICY_LINE_RE, '').trim()
      continue
    }
    const offer = offersOptions && OPTION_LINE_RE.test(line)
    for (const t of sentencesOfLine(line)) out.push({ text: t, offer })
  }
  return out
}

// A sentence that is not a promise: a question, an offer, a condition, an
// action tied to an event that has not happened yet, or one that waits on
// the customer, or one that says it is being reviewed.
function isConditional(sentence) {
  return sentence.endsWith('?') || NOT_A_PROMISE_RE.test(sentence) || EVENT_WINDOW_RE.test(sentence) || CUSTOMER_FIRST_RE.test(sentence) || UNDER_REVIEW_RE.test(sentence)
}

// The kinds a reply promises: [{ kind, tense, sentence }] in PROMISE_KINDS
// order, one per kind ('future' wins over 'done' when both appear).
// sentence is the reply's own sentence that made it (2026-10-27, quoted by
// the Send check so the person sees why); it is never logged.
// A reply that says the event its steps wait on has not happened yet
// (NOT_YET_RE) promises nothing, of any kind (2026-10-27).
// returnLabelOnOrder (2026-10-28): whether the order has a return label
// (true / false), for a return-label statement (LABEL_STATEMENTS above);
// omitted, its words decide. A kind found only by such a statement is marked
// statement: true (the primary agent draft warns on it instead of falling
// back, agent-draft-primary.js).
export function findPromisedActions(text, { returnLabelOnOrder = null } = {}) {
  const found = new Map()
  for (const p of findPromiseSentences(text, { returnLabelOnOrder })) {
    const had = found.get(p.kind)
    if (!had || (had.tense === 'done' && p.tense === 'future') || (had.statement && !p.statement && had.tense === p.tense)) found.set(p.kind, p)
  }
  return PROMISE_KINDS.filter((k) => found.has(k)).map((kind) => ({ kind, tense: found.get(kind).tense, sentence: found.get(kind).sentence, ...(found.get(kind).statement ? { statement: true } : {}) }))
}

// Every promise sentence (2026-10-28): [{ kind, tense, sentence,
// statement? }], the return-label statements first (statement: true), then
// one per kind per sentence of the promise patterns, in reply order ('future'
// wins in a sentence that has both). The same reading as
// findPromisedActions(), which keeps one per kind; the agent draft's
// repeated-line check (server/src/ai/agent-draft-checks.js's
// removeRepeatedDoneLines()) needs every sentence.
export function findPromiseSentences(text, { returnLabelOnOrder = null } = {}) {
  const out = []
  const sentences = sentencesOf(text)
  for (const { text: sentence, offer } of sentences) {
    if (offer) continue
    const tense = labelStatementTense(sentence, returnLabelOnOrder)
    if (tense) out.push({ kind: 'return_label', tense, sentence, statement: true })
  }
  if (sentences.some(({ text: s }) => !/\bif\b/i.test(s) && NOT_YET_RE.test(s))) return out
  for (const { text: sentence, offer } of sentences) {
    if (isConditional(sentence)) continue
    for (const kind of PROMISE_KINDS) {
      let tense = null
      for (const p of PATTERNS[kind]) {
        if (offer && p.tense === 'future') continue
        if (!p.re.test(sentence)) continue
        if (!tense || p.tense === 'future') tense = p.tense
      }
      if (tense) out.push({ kind, tense, sentence })
    }
  }
  return out
}

// --- The Send check ----------------------------------------------------------

export const PROMISE_UNPROCESSED_ERROR = 'promise_step_unprocessed'
export const PROMISE_NO_STEP_ERROR = 'promise_without_step'

// The reply's sentence behind a promise, quoted (2026-10-27): "a refund is
// being issued ("We've issued your refund of $45.00.")", so the person sees
// why. quotes: { [kind]: sentence } (findPromisedActions()'s sentence), or
// nothing for the words alone. A long sentence is cut at QUOTE_MAX.
const QUOTE_MAX = 160
export function quotedSentence(sentence) {
  const t = String(sentence ?? '').replace(/\s+/g, ' ').trim()
  if (!t) return ''
  return t.length > QUOTE_MAX ? `${t.slice(0, QUOTE_MAX - 1).trimEnd()}…` : t
}
// { [kind]: sentence } from findPromisedActions()'s list.
export function promiseQuotes(promised = []) {
  return Object.fromEntries((promised ?? []).filter((p) => p?.kind && p?.sentence).map((p) => [p.kind, p.sentence]))
}
// "a refund is being issued", with its quote when there is one.
export function promiseWords(kind, sentence) {
  const q = quotedSentence(sentence)
  return q ? `${PROMISE_KIND_WORDS[kind]} ("${q}")` : PROMISE_KIND_WORDS[kind]
}
// "the refund was already processed", with its quote (2026-10-28).
export function promiseDoneWords(kind, sentence) {
  const q = quotedSentence(sentence)
  return q ? `${PROMISE_DONE_WORDS[kind]} ("${q}")` : PROMISE_DONE_WORDS[kind]
}

function wordsFor(kinds, quotes = {}) {
  const words = kinds.map((k) => promiseWords(k, quotes?.[k]))
  return words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words.at(-1)}` : words[0]
}

// "This reply says a replacement is being sent, but that step hasn't been
// processed. Process the step first, or edit the reply."
export function promiseUnprocessedMessage(kinds, quotes = {}) {
  const many = kinds.length > 1
  return `This reply says ${wordsFor(kinds, quotes)}, but ${many ? 'those steps haven\'t' : 'that step hasn\'t'} been processed. Process the ${many ? 'steps' : 'step'} first (or use Process all steps and send reply), or edit the reply.`
}
export function promiseWithoutStepMessage(kinds, quotes = {}) {
  const names = kinds.map((k) => PROMISE_STEP_NAMES[k])
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names.at(-1)}` : names[0]
  return `This reply says ${wordsFor(kinds, quotes)}, but no ${list} step is on this contact. Add it from Manual Actions and process it, or edit the reply. Send anyway?`
}

// The Send check. text: the reply being sent. proposedActions: the
// suggestion's steps. processedIndexes: the indexes of this suggestion's
// steps that ran successfully. processedTypes: the types of steps that ran
// successfully on this contact recently, on any of its suggestions (a step
// processed before the draft was regenerated).
// Returns null, or { error, kinds, message, confirmable }:
//   - a promised kind with a step CXHub processes (one with an API call) on
//     the suggestion, none of them processed (and nothing of that kind
//     processed on the contact): PROMISE_UNPROCESSED_ERROR, never
//     confirmable;
//   - a promised kind whose only steps are console steps (isManualStep(),
//     no API call) is not checked: never a block and never a confirm, Send
//     just sends (docs/decisions/2026-10-27-no-manual-step-confirm.md);
//   - a promise of what is happening ('future') with no processable step of
//     its kind and nothing of that kind processed on the contact:
//     PROMISE_NO_STEP_ERROR, confirmable ("Send anyway?"). A reply that says
//     it has already happened, with no step, is not checked: it can be done
//     outside CXHub (in TRQ or on eBay).
//
// uncheckedIndexes (2026-10-26, the agent draft panel's step checkboxes,
// step-checks.js): a step the person unchecked won't be processed, so it is
// treated as missing. When every step of a promised kind is unchecked, the
// check asks first as for a missing step, naming the unchecked step(s).
// returnLabelOnOrder (2026-10-28): whether the suggestion's order had a
// return label when it was built (modelMeta.returnLabelOnOrder), for a
// return-label statement ("Check your email for a prepaid UPS return
// label"); null or omitted, its words decide.
export function promiseSendCheck({ text, proposedActions = [], processedIndexes = [], processedTypes = [], uncheckedIndexes = [], returnLabelOnOrder = null } = {}) {
  const promised = findPromisedActions(text, { returnLabelOnOrder })
  if (!promised.length) return null
  const quotes = promiseQuotes(promised)
  const done = new Set((processedIndexes ?? []).map(Number))
  const unchecked = new Set((uncheckedIndexes ?? []).map(Number))
  const doneKinds = new Set((processedTypes ?? []).flatMap((t) => stepPromiseKinds(t)))
  const unprocessed = []
  const noStep = []
  const uncheckedOnly = []
  const uncheckedSteps = []
  for (const { kind, tense } of promised) {
    if (doneKinds.has(kind)) continue
    const all = (proposedActions ?? []).map((a, i) => ({ a, i })).filter(({ a }) => isProcessableStep(a) && actionPromiseKinds(a).includes(kind))
    const steps = all.filter(({ i }) => !unchecked.has(i) || done.has(i))
    if (steps.some(({ i }) => done.has(i))) continue
    if (steps.length) {
      // Only console steps (no API call): nothing CXHub can process, so Send
      // just sends (2026-10-27, no confirm).
      if (steps.some(({ a }) => !isManualStep(a))) unprocessed.push(kind)
    } else if (tense === 'future') {
      if (all.length) {
        uncheckedOnly.push(kind)
        for (const s of all) if (!uncheckedSteps.some((u) => u.i === s.i)) uncheckedSteps.push(s)
      } else {
        noStep.push(kind)
      }
    }
  }
  if (unprocessed.length) return { error: PROMISE_UNPROCESSED_ERROR, kinds: unprocessed, message: promiseUnprocessedMessage(unprocessed, quotes), confirmable: false }
  if (uncheckedOnly.length) {
    const steps = uncheckedSteps.sort((x, y) => x.i - y.i).map(({ a, i }) => ({ index: i, label: a?.label ?? a?.type ?? null }))
    return { error: PROMISE_NO_STEP_ERROR, kinds: [...uncheckedOnly, ...noStep], message: promiseUncheckedMessage(uncheckedOnly, steps, noStep, quotes), confirmable: true, uncheckedSteps: steps }
  }
  if (noStep.length) return { error: PROMISE_NO_STEP_ERROR, kinds: noStep, message: promiseWithoutStepMessage(noStep, quotes), confirmable: true }
  return null
}

// "This reply says a replacement is being sent, but the step that does it,
// "Create replacement ...", is unchecked, so it won't be processed. Check the
// step, or edit the reply. Send anyway?"
export function promiseUncheckedMessage(kinds, steps = [], missingKinds = [], quotes = {}) {
  const quoted = steps.map((s) => `"${s.label ?? 'Step'}"`)
  const list = quoted.length > 1 ? `${quoted.slice(0, -1).join(', ')} and ${quoted.at(-1)}` : quoted[0]
  const many = steps.length > 1
  const head = `This reply says ${wordsFor(kinds, quotes)}, but the ${many ? 'steps that do it,' : 'step that does it,'} ${list}, ${many ? 'are' : 'is'} unchecked, so ${many ? 'they' : 'it'} won't be processed.`
  const missing = missingKinds.length ? ` It also says ${wordsFor(missingKinds, quotes)}, and no step on this contact does that.` : ''
  return `${head}${missing} Check the ${many ? 'steps' : 'step'}, or edit the reply. Send anyway?`
}

// The kinds a reply promises with no processable step of that kind on the
// suggestion (the primary agent draft's check, before anything is
// processed). [] when every promise has its step. Only what is happening
// ('future', 2026-10-28): a reply that says it already happened ("we've
// processed your refund") needs no step, as at Send; the agent draft checks
// such a statement against the order's facts instead. statements
// (2026-10-28): false leaves out a kind found only by a return-label
// statement (the primary draft warns on it instead).
export function promisesWithoutReadyStep(text, proposedActions = [], { statements = true, returnLabelOnOrder = null } = {}) {
  return findPromisedActions(text, { returnLabelOnOrder })
    .filter((p) => p.tense === 'future')
    .filter((p) => statements || !p.statement)
    .map((p) => p.kind)
    .filter((kind) => !(proposedActions ?? []).some((a) => isProcessableStep(a) && actionPromiseKinds(a).includes(kind)))
}

// --- Send warnings (2026-10-30) -----------------------------------------------
//
// docs/decisions/2026-10-30-send-warns-not-blocks.md: the promise check never
// stops Send. It warns; the CSR reads the warning and can Send anyway, which
// sends the reply exactly as written and processes nothing.
// promiseSendWarnings() is the one reading of it, used by the panel before
// Send and by the server's decision and retry-send routes. promiseSendCheck()
// above is the earlier refusal shape, kept for its readers and tests.

export const PROMISE_WARNING_CODES = Object.freeze({
  // The reply says it is happening; its step is on the contact, not processed.
  unprocessed: 'promise_step_unprocessed',
  // The reply says it is happening; no step of its kind is on the contact.
  noStep: 'promise_without_step',
  // The reply says it is happening; every step of its kind is unchecked.
  unchecked: 'promise_step_unchecked',
  // The reply says it already happened; its step is on the contact, not
  // processed. The text says whether the order shows it.
  donePending: 'promise_done_step_pending',
  // The reply says it already happened; no step of its kind on the contact,
  // and the order (as read) shows none (2026-10-30).
  doneNoStep: 'promise_done_no_step',
})

// "The reply says the refund was processed" (a done statement, in short).
export const PROMISE_DONE_SHORT = Object.freeze({
  refund: 'the refund was processed',
  replacement: 'the replacement was sent',
  return_label: 'the return label was sent',
  cancellation: 'the order was cancelled',
})
// "... but the order shows no refund".
export const PROMISE_NOT_SHOWN_WORDS = Object.freeze({
  refund: 'no refund',
  replacement: 'no replacement order',
  return_label: 'no return label',
  cancellation: 'no cancellation',
})

const money = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : `$${Number(v).toFixed(2)}`)
// "10/30/2026" from an ISO date or date-time (its calendar day as written),
// or an already formatted M/D/YYYY.
export function evidenceDate(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''))
  if (m) return `${m[2]}/${m[3]}/${m[1]}`
  const m2 = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(v ?? '').trim())
  return m2 ? `${m2[1].padStart(2, '0')}/${m2[2].padStart(2, '0')}/${m2[3]}` : null
}

// known: whether the data read says anything about that kind at all (an
// order in the facts snapshot, a template fill's refund read, a known return
// label state); "the order shows none" is said only when it is known.
const EMPTY_EVIDENCE = () => ({ refund: [], replacement: [], cancellation: [], return_label: [], known: { refund: false, replacement: false, cancellation: false, return_label: false } })

// What the order shows already happened, from what the suggestion was built
// or refreshed with (no call of its own): { refund, replacement,
// cancellation, return_label }, each a list of { number, amount, date },
// newest first. Read from:
//   - the Templates fill's refund document (the Refund Confirmation's "Filled
//     from RTN…" Activity Log note, routes/ai.js, an entry with
//     refundDocument), read when the CSR inserted the template, so the
//     newest read of the order there is;
//   - modelMeta.factsSnapshot (facts-snapshot.js, at generation): the
//     order's refund documents, its replacement order, its cancellation and
//     status;
//   - modelMeta.returnLabelOnOrder (a return label on the order).
export function orderDoneEvidence(modelMeta) {
  const out = EMPTY_EVIDENCE()
  const meta = modelMeta && typeof modelMeta === 'object' ? modelMeta : {}
  const calls = Array.isArray(meta.apiCalls) ? meta.apiCalls : []
  const addRefund = (d) => {
    if (d.number && out.refund.some((x) => x.number === d.number)) return
    out.refund.push(d)
  }
  for (let i = calls.length - 1; i >= 0; i--) {
    const d = calls[i]?.refundDocument
    if (d && typeof d === 'object' && (d.number || d.amount != null)) addRefund({ number: d.number ?? null, amount: d.amount ?? null, date: d.date ?? null })
  }
  const snap = meta.factsSnapshot && typeof meta.factsSnapshot === 'object' ? meta.factsSnapshot : null
  const docs = Array.isArray(snap?.documents) ? snap.documents : []
  const byDateDesc = (a, b) => String(b.date ?? '').localeCompare(String(a.date ?? ''))
  docs.filter((d) => d?.kind === 'refund' && (Number(d.amount) || 0) > 0)
    .map((d) => ({ number: d.number ?? null, amount: d.amount ?? null, date: d.date ?? null }))
    .sort(byDateDesc)
    .forEach(addRefund)
  out.replacement.push(...docs.filter((d) => d?.kind === 'replacement').map((d) => ({ number: d.number ?? null, amount: null, date: d.date ?? null })))
  out.cancellation.push(...docs.filter((d) => d?.kind === 'cancellation').map((d) => ({ number: null, amount: null, date: d.date ?? null })))
  if (!out.cancellation.length && /cancel/i.test(String(snap?.order?.status ?? ''))) out.cancellation.push({ number: null, amount: null, date: null })
  if (meta.returnLabelOnOrder === true) out.return_label.push({ number: null, amount: null, date: null })
  const orderRead = Boolean(snap?.order)
  out.known = {
    refund: orderRead || calls.some((c) => c?.refundDocument || c?.refundDocumentChecked === true),
    replacement: orderRead,
    cancellation: orderRead,
    return_label: typeof meta.returnLabelOnOrder === 'boolean',
  }
  return out
}

// "RTN123 ($178.55, 10/30/2026)", "replacement order 456 (10/29/2026)", "the
// order cancelled on 10/29/2026", "a return label". Order data shown to the
// CSR in the warning; never logged.
export function evidenceWords(kind, doc) {
  const date = evidenceDate(doc?.date)
  if (kind === 'refund') {
    const inner = [money(doc?.amount), date].filter(Boolean).join(', ')
    return `${doc?.number ?? 'a refund'}${inner ? ` (${inner})` : ''}`
  }
  if (kind === 'replacement') return `replacement order${doc?.number ? ` ${doc.number}` : ''}${date ? ` (${date})` : ''}`
  if (kind === 'cancellation') return `the order cancelled${date ? ` on ${date}` : ''}`
  return 'a return label'
}

// "The reply says the refund was processed. The order shows RTN123 ($178.55,
// 10/30/2026), but the refund step on this contact wasn't processed in CXHub.
// Send anyway, or remove the step if it isn't needed." / "The reply says the
// refund was processed, but the order shows no refund. Process the step, or
// Send anyway."
export function promiseDonePendingMessage(kind, evidence = []) {
  const doc = (evidence ?? [])[0]
  if (doc) return `The reply says ${PROMISE_DONE_SHORT[kind]}. The order shows ${evidenceWords(kind, doc)}, but the ${PROMISE_STEP_NAMES[kind]} step on this contact wasn't processed in CXHub. Send anyway, or remove the step if it isn't needed.`
  return `The reply says ${PROMISE_DONE_SHORT[kind]}, but the order shows ${PROMISE_NOT_SHOWN_WORDS[kind]}. Process the step, or Send anyway.`
}
export function promiseUnprocessedWarningMessage(kind, sentence) {
  return `This reply says ${promiseWords(kind, sentence)}, but the ${PROMISE_STEP_NAMES[kind]} step hasn't been processed. Process the step, or Send anyway to send the reply as written (the step stays as it is).`
}
export function promiseNoStepWarningMessage(kind, sentence) {
  return `This reply says ${promiseWords(kind, sentence)}, but no ${PROMISE_STEP_NAMES[kind]} step is on this contact. Add it from Other Actions and process it, or Send anyway.`
}
export function promiseUncheckedWarningMessage(kind, sentence, label) {
  return `This reply says ${promiseWords(kind, sentence)}, but the step that does it, "${label ?? 'Step'}", is unchecked, so it won't be processed. Check the step, or Send anyway.`
}

// The promise warnings for a reply: [{ code, text, kind, index?, fixes }]
// (the shape of send-warnings.js's warnings), at most one per promised kind,
// in PROMISE_KINDS order. Never a stop.
//   text, proposedActions, processedIndexes, processedTypes, uncheckedIndexes,
//   returnLabelOnOrder: as promiseSendCheck() above;
//   notNeededIndexes: steps the CSR marked not needed for this reply
//     (step-blocks.js's csr_marked_not_needed): treated as not on the contact;
//   evidence: orderDoneEvidence() of the suggestion's modelMeta.
// A promise of what is happening ('future'):
//   - its step on the contact, not processed: promise_step_unprocessed, fix
//     "Process the step";
//   - only unchecked steps: promise_step_unchecked, fix "Check the step";
//   - no step: promise_without_step.
// A statement that it already happened ('done'):
//   - its step on the contact, not processed: promise_done_step_pending,
//     saying what the order shows; fixes "Mark this step not needed" (and
//     "Process the step" first when the order shows nothing);
//   - no step (or the step marked not needed) and the order, as read,
//     shows none: promise_done_no_step (2026-10-30, Jenn); when it shows it,
//     or the read data says nothing about that kind: nothing (it can be
//     done outside CXHub).
// A console step (isManualStep()) is never checked, as before.
export function promiseSendWarnings({ text, proposedActions = [], processedIndexes = [], processedTypes = [], uncheckedIndexes = [], notNeededIndexes = [], returnLabelOnOrder = null, evidence = null } = {}) {
  const promised = findPromisedActions(text, { returnLabelOnOrder })
  if (!promised.length) return []
  const done = new Set((processedIndexes ?? []).map(Number))
  const unchecked = new Set((uncheckedIndexes ?? []).map(Number))
  const notNeeded = new Set((notNeededIndexes ?? []).map(Number))
  const doneKinds = new Set((processedTypes ?? []).flatMap((t) => stepPromiseKinds(t)))
  const ev = evidence ?? EMPTY_EVIDENCE()
  const out = []
  for (const { kind, tense, sentence } of promised) {
    if (doneKinds.has(kind)) continue
    const all = (proposedActions ?? []).map((a, i) => ({ a, i })).filter(({ a, i }) => isProcessableStep(a) && actionPromiseKinds(a).includes(kind) && !notNeeded.has(i))
    if (all.some(({ i }) => done.has(i))) continue
    const live = all.filter(({ i }) => !unchecked.has(i))
    if (live.length) {
      const pending = live.filter(({ a }) => !isManualStep(a))
      if (!pending.length) continue
      const index = pending[0].i
      if (tense === 'done') {
        const shown = (ev[kind] ?? []).length > 0
        out.push({
          code: PROMISE_WARNING_CODES.donePending, kind, index, text: promiseDonePendingMessage(kind, ev[kind]),
          fixes: [...(shown ? [] : [{ kind: 'process_step', label: 'Process the step', index }]), { kind: 'mark_not_needed', label: 'Mark this step not needed', index }],
        })
      } else {
        out.push({ code: PROMISE_WARNING_CODES.unprocessed, kind, index, text: promiseUnprocessedWarningMessage(kind, sentence), fixes: [{ kind: 'process_step', label: 'Process the step', index }] })
      }
    } else if (tense === 'future') {
      if (all.length) {
        const index = all[0].i
        out.push({ code: PROMISE_WARNING_CODES.unchecked, kind, index, text: promiseUncheckedWarningMessage(kind, sentence, all[0].a?.label ?? all[0].a?.type), fixes: [{ kind: 'check_step', label: 'Check the step', index }] })
      } else {
        out.push({ code: PROMISE_WARNING_CODES.noStep, kind, text: promiseNoStepWarningMessage(kind, sentence), fixes: [] })
      }
    } else if (ev.known?.[kind] && !(ev[kind] ?? []).length) {
      // Done, no step to do it, and the order (as read) shows none
      // (2026-10-30, Jenn). Said only when the read data covers the kind.
      out.push({ code: PROMISE_WARNING_CODES.doneNoStep, kind, text: promiseDoneNoStepMessage(kind), fixes: [] })
    }
  }
  return out
}

// "The reply says the refund was processed, but there's no refund step on
// this contact and the order shows no refund."
export function promiseDoneNoStepMessage(kind) {
  return `The reply says ${PROMISE_DONE_SHORT[kind]}, but there's no ${PROMISE_STEP_NAMES[kind]} step on this contact and the order shows ${PROMISE_NOT_SHOWN_WORDS[kind]}. Edit the reply, or Send anyway.`
}
